import React, { useCallback, useMemo, useState } from 'react';
import { Button, Dropdown, message, Tooltip } from 'antd';
import {
  ClearOutlined,
  DownOutlined,
  FilterOutlined,
  SaveOutlined,
  StarFilled,
  StarOutlined,
  UpOutlined,
} from '@ant-design/icons';

const MAX_VISIBLE_CHIPS = 4;

/**
 * نوار فیلتر Sticky گزارش‌ها
 * — خلاصه چیپ‌ها همیشه بالای صفحه می‌ماند
 * — پاک‌سازی همه / ذخیره نما / نمای من
 */
const ReportFiltersCard = ({
  children,
  title = 'فیلترها',
  summaryChips = [],
  onClearAll,
  storageKey,
  getSnapshot,
  onApplySnapshot,
  defaultExpanded = false,
  className = '',
}) => {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const [hasSavedView, setHasSavedView] = useState(() => {
    if (!storageKey || typeof window === 'undefined') return false;
    try {
      return Boolean(window.localStorage.getItem(storageKey));
    } catch {
      return false;
    }
  });

  const chips = useMemo(
    () => (Array.isArray(summaryChips) ? summaryChips.filter(Boolean) : []),
    [summaryChips],
  );

  const visibleChips = chips.slice(0, MAX_VISIBLE_CHIPS);
  const extraCount = Math.max(0, chips.length - MAX_VISIBLE_CHIPS);

  const handleSaveView = useCallback(() => {
    if (!storageKey || typeof getSnapshot !== 'function') {
      message.warning('ذخیره نما برای این تب در دسترس نیست');
      return;
    }
    try {
      const snapshot = getSnapshot();
      window.localStorage.setItem(storageKey, JSON.stringify(snapshot));
      setHasSavedView(true);
      message.success('«نمای من» ذخیره شد');
    } catch (err) {
      console.error('Save filter view failed:', err);
      message.error('ذخیره نما ناموفق بود');
    }
  }, [storageKey, getSnapshot]);

  const handleLoadMyView = useCallback(() => {
    if (!storageKey || typeof onApplySnapshot !== 'function') {
      message.warning('نمای ذخیره‌شده‌ای وجود ندارد');
      return;
    }
    try {
      const raw = window.localStorage.getItem(storageKey);
      if (!raw) {
        message.info('هنوز نمایی ذخیره نکرده‌اید — ابتدا «ذخیره نما» را بزنید');
        return;
      }
      const parsed = JSON.parse(raw);
      onApplySnapshot(parsed);
      message.success('نمای من اعمال شد');
    } catch (err) {
      console.error('Load filter view failed:', err);
      message.error('بارگذاری نما ناموفق بود');
    }
  }, [storageKey, onApplySnapshot]);

  const handleClearSavedView = useCallback(() => {
    if (!storageKey) return;
    try {
      window.localStorage.removeItem(storageKey);
      setHasSavedView(false);
      message.success('نمای ذخیره‌شده حذف شد');
    } catch {
      message.error('حذف نما ناموفق بود');
    }
  }, [storageKey]);

  const myViewMenu = {
    items: [
      {
        key: 'apply',
        icon: hasSavedView ? <StarFilled className="text-amber-500" /> : <StarOutlined />,
        label: 'اعمال نمای من',
        onClick: handleLoadMyView,
      },
      {
        key: 'clear-saved',
        icon: <ClearOutlined />,
        label: 'حذف نمای ذخیره‌شده',
        disabled: !hasSavedView,
        onClick: handleClearSavedView,
      },
    ],
  };

  return (
    <div
      className={`sticky top-0 z-30 mb-5 custom-font -mx-1 px-1 ${className}`}
      style={{
        // فاصله کوچک از لبه اسکرول تا سایه کامل دیده شود
      }}
    >
      <div
        className="
          rounded-xl border border-slate-200/90 dark:border-slate-600
          bg-white/95 dark:bg-slate-900/95 backdrop-blur-md
          shadow-[0_4px_20px_rgba(15,23,42,0.06)] dark:shadow-[0_4px_24px_rgba(0,0,0,0.35)]
          overflow-hidden
        "
      >
        {/* نوار فشرده Sticky */}
        <div className="flex flex-wrap items-center gap-2 px-3 py-2.5 sm:px-3.5">
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            className="
              inline-flex items-center gap-1.5 shrink-0 rounded-lg px-2 py-1
              text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800
              transition-colors
            "
            aria-expanded={expanded}
            aria-label={expanded ? 'بستن فیلترها' : 'باز کردن فیلترها'}
          >
            <FilterOutlined className="text-[#0D887E]" />
            <span className="text-xs font-medium hidden sm:inline">{title}</span>
            {expanded ? (
              <UpOutlined className="text-[10px] opacity-60" />
            ) : (
              <DownOutlined className="text-[10px] opacity-60" />
            )}
          </button>

          <div className="flex-1 min-w-0 flex items-center gap-1.5 overflow-x-auto custom-scrollbar py-0.5">
            {visibleChips.length ? (
              <>
                {visibleChips.map((chip, idx) => (
                  <React.Fragment key={`${chip}-${idx}`}>
                    {idx > 0 ? (
                      <span className="text-slate-300 dark:text-slate-600 text-xs shrink-0 select-none">
                        |
                      </span>
                    ) : null}
                    <span
                      className="
                        shrink-0 inline-flex items-center rounded-md
                        bg-slate-100/90 dark:bg-slate-800 text-slate-700 dark:text-slate-200
                        px-2 py-0.5 text-[11px] sm:text-xs font-medium whitespace-nowrap
                      "
                    >
                      {chip}
                    </span>
                  </React.Fragment>
                ))}
                {extraCount > 0 ? (
                  <>
                    <span className="text-slate-300 dark:text-slate-600 text-xs shrink-0">|</span>
                    <button
                      type="button"
                      onClick={() => setExpanded(true)}
                      className="
                        shrink-0 rounded-md bg-teal-50 dark:bg-teal-950/40
                        text-teal-700 dark:text-teal-300
                        px-2 py-0.5 text-[11px] sm:text-xs font-medium
                        hover:bg-teal-100 dark:hover:bg-teal-900/50 transition-colors
                      "
                    >
                      +{extraCount} فیلتر
                    </button>
                  </>
                ) : null}
              </>
            ) : (
              <span className="text-xs text-slate-400">بدون فیلتر فعال</span>
            )}
          </div>

          <div className="flex items-center gap-1 shrink-0">
            {storageKey && onApplySnapshot ? (
              <Dropdown menu={myViewMenu} trigger={['click']} placement="bottomLeft">
                <Button
                  size="small"
                  type={hasSavedView ? 'default' : 'text'}
                  className="custom-font text-xs h-7 px-2"
                  icon={
                    hasSavedView ? (
                      <StarFilled className="text-amber-500" />
                    ) : (
                      <StarOutlined />
                    )
                  }
                >
                  <span className="hidden sm:inline">نمای من</span>
                </Button>
              </Dropdown>
            ) : null}

            {storageKey && getSnapshot ? (
              <Tooltip title="ذخیره فیلترهای فعلی به‌عنوان نمای من">
                <Button
                  size="small"
                  type="text"
                  className="custom-font text-xs h-7 px-2"
                  icon={<SaveOutlined />}
                  onClick={handleSaveView}
                >
                  <span className="hidden md:inline">ذخیره نما</span>
                </Button>
              </Tooltip>
            ) : null}

            {onClearAll ? (
              <Tooltip title="پاک کردن همه فیلترها">
                <Button
                  size="small"
                  type="text"
                  danger
                  className="custom-font text-xs h-7 px-2"
                  icon={<ClearOutlined />}
                  onClick={onClearAll}
                >
                  <span className="hidden md:inline">پاک‌سازی همه</span>
                </Button>
              </Tooltip>
            ) : null}
          </div>
        </div>

        {/* فرم کامل فیلترها */}
        {expanded && children ? (
          <div className="border-t border-slate-100 dark:border-slate-700 px-3 py-3 sm:px-3.5 bg-slate-50/50 dark:bg-slate-800/30">
            {children}
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default ReportFiltersCard;
